import type { IScreenDesign, IScreenDesignDictionary, IScreenDesignDto } from "@kuika/kuika-cl-model";
import { CommonHelperFunctions, DashboardState, ESdChangeReason, PlaceToType } from "@kuika/kuika-cl-model";
import type { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { ScreenService } from "../../services/screen-service";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";
import { KMainFunctions } from "./main-functions";
import { PropertySdHelper } from "./property-sd-helper";
import { collectDefinedActions } from "./screen-defined-actions";
import { ScreenDesignHelper } from "./screen-design-helper";
import { insertComponentIntoHost, removeComponentFromTree } from "./screen-tree-ops";

/**
 * A screen held in memory and edited WITHOUT being opened in the designer.
 *
 * ## Why this exists
 *
 * The workflow screen builder edits the task screen of the step the user selected in a list.
 * That screen is almost never the one on the canvas, and opening it is not an option: the
 * designer's only "open a screen" path changes the URL and re-opens the whole project
 * (`ScreenProvider.loadScreen`), which would throw the user out of the process editor on every
 * click in a sidebar.
 *
 * At the same time every writer in this codebase reaches the screen through exactly one triple —
 * `screenDesignDictionary`, `getScreenDesign()`, `setScreenDesign()` — obtained from
 * `getValues(sdInstance)`. That is the whole seam. A session therefore exposes the same triple
 * over its own in-memory copy, and `resolveEditTarget` decides which one a writer gets.
 *
 * ## What it deliberately does NOT do
 *
 * - **No global store.** The open screen lives in a redux store with the undo/redo history
 *   (`sdStore`), and the provider's `setScreenDesign` dispatches into it. A session never
 *   touches it, so a detached edit can never appear as an undo step on the open screen — or
 *   worse, be written to the open screen's tree.
 * - **No lock, no navigation, no project reload.** The fetch is a plain GET, the same choice
 *   `ScreenProvider.refreshOpenScreenDesign` makes for the same reason: going through
 *   `getScreenDesignFromServer` would raise the "screen in use" dialog for a screen the user is
 *   merely inspecting.
 * - **No save.** Persistence is not a session concern: the workflow screens are written by the
 *   backend during Apply Screens, in one pass per step, and that pass owns the collab flush and
 *   the in-use lock. A session that could PUT the tree itself would race it.
 *
 * ## The open screen is never a session
 *
 * If the screen in question IS the one on the canvas, the live provider is the correct target —
 * two live copies of one screen diverge silently, and the canvas copy is the one the user can
 * see. `openDetachedScreenSession` refuses that id and `resolveEditTarget` routes it to the
 * provider.
 */
export interface IDetachedScreenSession {
  /** The screen this session holds. */
  readonly screenId: string;
  /** The screen's own name, as the server returned it. */
  readonly screenName: string;
  /** Bumped on every `setScreenDesign`, so a view can tell that the tree changed. */
  readonly revision: number;
  /** The reason and target of the last write, mirrored so hosted editors re-render as on canvas. */
  readonly lastChangeReason: ESdChangeReason | undefined;
  readonly lastChangeKuikaId: string | undefined;
  /** Called after every write. Returns an unsubscribe. */
  subscribe(listener: () => void): () => void;
  /** The in-memory tree. Same object identity across reads until it is replaced. */
  getScreenDesign(): IScreenDesign;
  /** Replace (or re-publish, after an in-place mutation) the tree and rebuild the dictionary. */
  setScreenDesign(screenDesign: IScreenDesign, changeReason: ESdChangeReason, kuikaId?: string): void;
  /** id → display item, rebuilt on every `setScreenDesign`. */
  readonly dictionary: IScreenDesignDictionary;
  /**
   * The live designer context, with every screen-scoped member pointed at THIS screen.
   *
   * Project-scoped members (the component catalog, action definitions, datasources, the project
   * itself) are passed through unchanged — they are properties of the project, not of a screen,
   * and a session must not fork them.
   */
  asContextState(
    live: IScreenDesignContextState,
    sdInstance: ScreenDesignInstance,
    selectedKuikaId?: number | null
  ): IScreenDesignContextState;
}

/**
 * WHICH TOOL IS THE USER ON — the answer every screen editor needs, and the one the process editor
 * gets wrong.
 *
 * The platform routes an action attach by `dashboardState` (`PropertySdHelper.saveActionToEvent`,
 * and the picker's own `createActionByEventName` mirrors it). Inside the process editor that
 * routing rewrites a component's page-load action into the WORKFLOW STEP's own init and attaches it
 * there — no error, and nothing appears on the screen the user was editing. Reported as "I click
 * the action and nothing happens".
 *
 * Nothing needs to be inferred for a screen editor: the target is a component id on a named
 * screen. So a scoped context states the design view and no selected sub-entity, and the
 * platform's own routing then lands on the component.
 *
 * Shared by BOTH scoped contexts — a detached screen and the open screen edited from the Forms
 * tab — because the routing does not care which of the two it is, and two copies of this would
 * drift.
 */
function screenEditingIdentity(): Partial<IScreenDesignContextState> {
  return {
    dashboardState: DashboardState.design,
    selectedWf: undefined,
    getSelectedWfStep: () => undefined,
    getSelectedSj: () => undefined,
    getSelectedApi: () => undefined,
    getSelectedApiMethod: () => undefined,
    getSelectedBluetoothMethod: () => undefined,
    activeEditingDatasource: undefined,
    // A Document component's authoring popup cannot be open for a screen edited from a list.
    isDocumentParameterAuthoringActive: () => false
  };
}

/**
 * The selection, for editors that ask "which component am I editing?".
 *
 * The platform's action authoring surface reads its target off the SELECTION rather than from a
 * prop. Inside a scoped context the selected component is whatever the screen builder's outline
 * selected — never the canvas's own selection, which may be a different component (or belong to a
 * different screen entirely).
 */
function screenEditingSelection(
  dictionary: IScreenDesignDictionary,
  selectedKuikaId: number | null | undefined
): Partial<IScreenDesignContextState> {
  const hasSelection: boolean = selectedKuikaId !== undefined && selectedKuikaId !== null;
  return {
    getSelectedDisplayItem: () =>
      hasSelection
        ? (dictionary[selectedKuikaId as number] as ReturnType<IScreenDesignContextState["getSelectedDisplayItem"]>)
        : undefined,
    hasASelectedComponent: hasSelection,
    selectedKuikaIds: hasSelection ? [String(selectedKuikaId)] : []
  };
}

/**
 * The screen writers, re-pointed at a scoped context.
 *
 * WHY THIS IS NOT OPTIONAL. Every one of these provider methods delegates to the provider's OWN
 * `PropertySdHelper`, whose delegate is the PROVIDER's state — not the context handed to the
 * editors. So a scoped context that merely says "design view" is read correctly and then WRITES
 * through a helper that still sees the process editor: `saveActionToEvent` re-reads
 * `dashboardState` from the provider, finds the workflow tool with a step selected, and attaches
 * the action to the STEP. Silently — the user clicks an action and nothing appears on the screen.
 * Measured on a real task screen; the first fix only re-pointed the reads and left this.
 *
 * Re-pointed rather than reimplemented: it is the platform's own helper, bound to the context the
 * editors are actually using. `sdInstance` is still passed, because the helper uses it for the
 * project-level StartProcess trigger index, which keys off the screen id it reads from the
 * delegate.
 */
function screenEditingWriters(
  getContext: () => IScreenDesignContextState,
  sdInstance: ScreenDesignInstance
): Partial<IScreenDesignContextState> {
  const helper = (): PropertySdHelper => new PropertySdHelper(getContext, sdInstance);
  return {
    applyPropertyGridChangesToDisplay: (params, changeReason): void => {
      helper().applyPropertyGridChangesToDisplay(params, changeReason);
    },
    changeSelectedPropertyType: (params): void => {
      helper().changeSelectedPropertyType(params);
    },
    saveActionToEvent: (kuikaId, action, event, changeReason) =>
      helper().saveActionToEvent(kuikaId, action, event, changeReason),
    deleteActionFromEvent: (kuikaId, action, event): void => {
      helper().deleteActionFromEvent(kuikaId, action, event);
    },
    addEventToDi: (kuikaId, event): void => {
      helper().addEventToDi(kuikaId, event);
    },
    saveValidationToIValidation: (kuikaId, validation, changeReason): void => {
      helper().saveValidationToIValidation(kuikaId, validation, changeReason);
    },
    deleteValidateFromIValidation: (kuikaId, validation): void => {
      helper().deleteValidationToIValidation(kuikaId, validation);
    },
    saveVisibilitytoDisplayItem: (kuikaId, visibility, changeReason): void => {
      helper().saveVisibilitytoDisplayItem(kuikaId, visibility, changeReason);
    },
    saveEditabilityToDisplayItem: (kuikaId, editability, changeReason): void => {
      helper().saveEditabilityToDisplayItem(kuikaId, editability, changeReason);
    }
  };
}

/**
 * The OPEN screen, scoped for editing from the Forms tab.
 *
 * The task screen is occasionally the one already on the canvas. There is no session then — the
 * live state IS the right tree, and a second copy of it would diverge — but the tool identity, the
 * selection AND the writers still have to be re-pointed, for the reasons above. The tree stays the
 * live one, so a write here is an ordinary canvas edit that the designer saves by itself.
 */
export function asScreenEditingContext(
  live: IScreenDesignContextState,
  sdInstance: ScreenDesignInstance,
  selectedKuikaId?: number | null
): IScreenDesignContextState {
  let scoped: IScreenDesignContextState;
  scoped = {
    ...live,
    ...screenEditingIdentity(),
    ...screenEditingSelection(live.screenDesignDictionary ?? {}, selectedKuikaId),
    ...screenEditingWriters((): IScreenDesignContextState => scoped, sdInstance)
  };
  return scoped;
}

/** Sessions by screen id. One per screen: a second copy of one screen is a divergence bug. */
const sessions: Map<string, IDetachedScreenSession> = new Map<string, IDetachedScreenSession>();

class DetachedScreenSession implements IDetachedScreenSession {
  readonly screenId: string;

  readonly screenName: string;

  revision: number = 0;

  lastChangeReason: ESdChangeReason | undefined;

  lastChangeKuikaId: string | undefined;

  private screen: IScreenDesign;

  private dict: IScreenDesignDictionary;

  private readonly listeners: Set<() => void> = new Set<() => void>();

  constructor(screenId: string, screen: IScreenDesign) {
    this.screenId = screenId;
    this.screenName = screen.name ?? "";
    this.screen = screen;
    this.dict = CommonHelperFunctions.createScreenDesignDict(screen);
  }

  get dictionary(): IScreenDesignDictionary {
    return this.dict;
  }

  getScreenDesign = (): IScreenDesign => this.screen;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return (): void => {
      this.listeners.delete(listener);
    };
  };

  setScreenDesign = (screenDesign: IScreenDesign, changeReason: ESdChangeReason, kuikaId?: string): void => {
    // `preprocessScreen` before the dictionary, exactly as the load path does: it is what
    // re-establishes `parentId` and the `current` symbols' `diId` after a subtree moves or is
    // inserted. Skipping it leaves a dictionary whose parent chain lies, and the ownership walk
    // that protects the platform's own regions reads precisely that chain.
    ScreenDesignHelper.preprocessScreen(screenDesign);
    this.screen = screenDesign;
    this.dict = CommonHelperFunctions.createScreenDesignDict(screenDesign);
    this.revision += 1;
    // Mirrored onto the context the hosted editors read, so the platform's own re-render gate
    // (`PureSdComponent.shouldComponentUpdate` → `checkNeedRender(changeReason)`) behaves exactly
    // as it does on the canvas. Without a changing reason, an editor keeps showing the value from
    // before its own write.
    this.lastChangeReason = changeReason;
    this.lastChangeKuikaId = kuikaId;
    this.listeners.forEach((listener: () => void): void => {
      listener();
    });
  };

  asContextState = (
    live: IScreenDesignContextState,
    sdInstance: ScreenDesignInstance,
    selectedKuikaId?: number | null
  ): IScreenDesignContextState => {
    // Captured rather than aliasing `this`: the dictionary is read through a closure because the
    // overlay exposes it as a GETTER (an object-literal getter's `this` is the literal, not the
    // session), and it must answer with the CURRENT dictionary after every write.
    const readDictionary = (): IScreenDesignDictionary => this.dictionary;
    const readScreen = (): IScreenDesign => this.getScreenDesign();
    const screenId: string = this.screenId;
    const screenName: string = this.screenName;
    const writeScreen = this.setScreenDesign;
    const readChangeReason = (): ESdChangeReason | undefined => this.lastChangeReason;
    const readChangeKuikaId = (): string | undefined => this.lastChangeKuikaId;

    /** Lazily bound to the overlay itself, so the helper reads and writes THIS screen. */
    let overlay: IScreenDesignContextState;

    const refuse = (member: string, because: string): never => {
      throw new Error(`${member} is not available on a detached screen session (screen "${screenId}"): ${because}`);
    };

    /** Acts on the canvas, not on a screen object — see the module header. */
    const refuseCanvasOp = (member: string): never =>
      refuse(
        member,
        `it is a canvas operation. It reaches the tree through the provider (or the drag-drop ` +
          `engine), so it would act on the screen the designer has OPEN. Build the subtree with ` +
          `ScreenDesignHelper against this session's screen instead.`
      );

    /** Belongs to the open screen's lifecycle: history, save, navigation. */
    const refuseOpenScreenOp = (member: string): never =>
      refuse(
        member,
        `it acts on the screen the designer has OPEN; a detached session has no canvas, no undo ` +
          `history and no save path of its own.`
      );

    overlay = {
      ...live,
      sdId: screenId,
      sdName: screenName,
      // The reason of the LAST write to this screen, and its target. See `setScreenDesign`.
      // `OtherSetStates` until this session is written to: it is the platform's neutral reason,
      // and the gate treats it as "re-read" rather than as a specific edit.
      changeReason: readChangeReason() ?? ESdChangeReason.OtherSetStates,
      changeReasonParameter: readChangeKuikaId(),

      // See `screenEditingIdentity`: without this an action attach is rewritten into the workflow
      // step's own init and never reaches the component.
      ...screenEditingIdentity(),

      // The seam itself.
      get screenDesignDictionary(): IScreenDesignDictionary {
        return readDictionary();
      },
      getScreenDesign: readScreen,
      setScreenDesign: writeScreen,

      // Provider methods that write the screen through the PROVIDER's own property helper —
      // re-pointed at this session. Shared with the open-screen scope; see `screenEditingWriters`.
      ...screenEditingWriters((): IScreenDesignContextState => overlay, sdInstance),

      // ── Screen-scoped readers that would otherwise answer about the open screen.
      getDefinedActions: (): ReturnType<IScreenDesignContextState["getDefinedActions"]> =>
        collectDefinedActions(readScreen(), live.actionDefinitions),
      // A detached screen has no selection: nothing is rendered, so nothing is selected. A writer
      // that needs a target must be given a kuikaId — inheriting the canvas's selection is how a
      // write lands on a component of the wrong screen.
      // See `screenEditingSelection`. Read through the session's CURRENT dictionary, so the
      // selected item stays correct after a write replaces it.
      ...screenEditingSelection(readDictionary(), selectedKuikaId),
      getSelectedDisplayItem: () =>
        selectedKuikaId === undefined || selectedKuikaId === null
          ? undefined
          : (readDictionary()[selectedKuikaId] as ReturnType<IScreenDesignContextState["getSelectedDisplayItem"]>),
      // The OPEN screen's master, and this is not that screen. An empty master is honest: a
      // reader that would offer the master's inputs offers none, rather than the wrong screen's.
      // (A detached session does not fetch its own master — nothing in the screen builder needs
      // one; if that changes, it is loaded here, not inherited.)
      masterSd: undefined,

      // ── ADD and REMOVE are screen-agnostic, and they are the one deliberate exception to the
      // refusals below.
      //
      // The platform's own writers call them. `applyTableFieldBinding` — the only path that binds a
      // table cell the way the property panel does — deletes the column's edit-mode renderer and
      // re-creates it for the field's type through exactly these two. A session that refuses them
      // cannot bind a cell at all, which is how a composed table shipped with columns that showed
      // nothing (KOA-18431). The mechanism is `screen-tree-ops`, which acts on the screen it is
      // handed and never on the canvas, so nothing here can reach the open screen.
      addChildItemByComponentName: (kuikaId: string, componentName: string, placeToType?: PlaceToType): void => {
        const hostKuikaId: number = Number.parseInt(String(kuikaId), 10);
        if (!Number.isFinite(hostKuikaId)) return;
        insertComponentIntoHost(overlay, sdInstance, {
          hostKuikaId,
          componentName,
          ...(placeToType === PlaceToType.PlaceToFirstChildItem ? { placeFirst: true } : {})
        });
      },
      deleteItemByKuikaId: (kuikaId: string): void => {
        const targetKuikaId: number = Number.parseInt(String(kuikaId), 10);
        if (!Number.isFinite(targetKuikaId)) return;
        removeComponentFromTree(overlay, targetKuikaId);
      },

      // ── Canvas / drag-drop tree operations. REFUSED, not passed through: every one of them
      // reaches the tree via the provider, so inheriting them would let a caller believe it was
      // restructuring this screen while the open one changed underneath the user.
      reOrderItems: (): never => refuseCanvasOp("reOrderItems"),
      cloneDI: (): never => refuseCanvasOp("cloneDI"),
      addNavigationComponent: (): never => refuseCanvasOp("addNavigationComponent"),
      detachForm: (): never => refuseCanvasOp("detachForm"),
      setSelectedDisplayItem: (): never => refuseCanvasOp("setSelectedDisplayItem"),
      setResponsiveRowColumnSettings: (): never => refuseCanvasOp("setResponsiveRowColumnSettings"),
      setColPropertiesForSmallSizes: (): never => refuseCanvasOp("setColPropertiesForSmallSizes"),
      resizeColumns: (): never => refuseCanvasOp("resizeColumns"),
      splitColumn: (): never => refuseCanvasOp("splitColumn"),

      // ── Open-screen lifecycle.
      saveScreen: (): never => refuseOpenScreenOp("saveScreen"),
      loadScreen: (): never => refuseOpenScreenOp("loadScreen"),
      refreshOpenScreenDesign: (): never => refuseOpenScreenOp("refreshOpenScreenDesign"),
      undo: (): never => refuseOpenScreenOp("undo"),
      redo: (): never => refuseOpenScreenOp("redo"),
      getSerializableScreenDesign: (): never => refuseOpenScreenOp("getSerializableScreenDesign")
    };

    return overlay;
  };
}

/**
 * Load a screen into a detached session, or return the one already loaded.
 *
 * Pass `reload` to re-fetch a session that is already open (after Apply Screens regenerated the
 * screen, for instance). Returns `undefined` when the screen cannot be fetched — the caller
 * decides what to show; a failed load is not an exception, the same posture as
 * `refreshOpenScreenDesign`.
 */
export async function openDetachedScreenSession(
  sdInstance: ScreenDesignInstance,
  screenId: string,
  reload: boolean = false
): Promise<IDetachedScreenSession | undefined> {
  if (!screenId) return undefined;
  const live: IScreenDesignContextState = getValues(sdInstance);
  if (live.sdId === screenId) {
    throw new Error(
      `Screen "${screenId}" is the screen currently open in the designer; edit it through the ` +
        `live context instead of a detached session (resolveEditTarget already does this).`
    );
  }

  const existing: IDetachedScreenSession | undefined = sessions.get(screenId);
  if (existing !== undefined && !reload) return existing;

  try {
    const response = await ScreenService.Get(screenId);
    const dto: IScreenDesignDto | undefined = response?.data;
    // ScreenService swallows transport errors and returns the error object, so the payload is
    // what says whether the fetch worked — not the absence of a throw.
    if (!dto?.id) return undefined;
    const screen: IScreenDesign = KMainFunctions.convertSDDTOToIScreenDesignInterface(dto);
    ScreenDesignHelper.preprocessScreen(screen);
    const session: IDetachedScreenSession = new DetachedScreenSession(screenId, screen);
    sessions.set(screenId, session);
    return session;
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
    return undefined;
  }
}

/** The session for `screenId`, if one is loaded. */
export function getDetachedScreenSession(screenId: string | null | undefined): IDetachedScreenSession | undefined {
  if (!screenId) return undefined;
  return sessions.get(screenId);
}

/** Drop one session. Its in-memory tree is discarded; nothing is persisted. */
export function closeDetachedScreenSession(screenId: string | null | undefined): void {
  if (!screenId) return;
  sessions.delete(screenId);
}

/** Drop every session — for the owning tool's unmount, and for test isolation. */
export function closeAllDetachedScreenSessions(): void {
  sessions.clear();
}

/**
 * The input shape of a designer action that MAY address a screen other than the open one.
 *
 * One optional member, inherited by the action inputs themselves, so a caller that means "this
 * other screen" says so in the payload rather than out of band. Omitting it is the old
 * behaviour, exactly: the open screen.
 */
export interface IScreenScopedInput {
  /** Screen to act on. Omitted (or the open screen's id) → the screen the designer has open. */
  screenId?: string | null;
}

/**
 * WHICH screen a designer write acts on.
 *
 * The single decision point, so no writer has to know that detached sessions exist:
 *
 *   - no screen id → the open screen, byte-identical to the behaviour before sessions existed;
 *   - the OPEN screen's id → the open screen (the canvas copy is the one the user can see);
 *   - a loaded session's id → that session;
 *   - anything else → THROWS.
 *
 * The throw is the important part. Falling back to the open screen for an unknown screen id is
 * the wrong-canvas failure: the caller says "screen A" and the platform quietly rewrites screen
 * B, which reads as data loss and cannot be undone by the user. A caller that names a screen
 * must open its session first.
 */
/**
 * Like {@link resolveEditTarget}, but answers `undefined` instead of throwing.
 *
 * For RENDER paths. A component that draws a property editor for a task screen cannot afford a
 * throw — an exception during render takes the whole designer down, which this codebase has
 * already paid for twice. It also must not silently fall back to the open screen: reading one
 * screen while the user believes they are looking at another is the wrong-canvas failure. So the
 * caller gets `undefined` and renders the absence.
 */
export function tryResolveEditTarget(
  sdInstance: ScreenDesignInstance,
  screenId?: string | null
): IScreenDesignContextState | undefined {
  try {
    return resolveEditTarget(sdInstance, screenId);
  } catch {
    return undefined;
  }
}

export function resolveEditTarget(
  sdInstance: ScreenDesignInstance,
  screenId?: string | null
): IScreenDesignContextState {
  const live: IScreenDesignContextState = getValues(sdInstance);
  if (!screenId || screenId === live.sdId) return live;

  const session: IDetachedScreenSession | undefined = sessions.get(screenId);
  if (session === undefined) {
    throw new Error(
      `Screen "${screenId}" is neither the open screen nor a loaded detached session, so there is ` +
        `nothing to edit. Open a detached session for it first.`
    );
  }
  return session.asContextState(live, sdInstance);
}
